import { gql, TypedDocumentNode } from "@apollo/client";
import type { CareerConsultantAssignment, CoverageSection, MessageTemplate, MessageTemplateType } from "./types";

export type UpsertSectionConsultantAssignmentResponse = {
  upsertSectionConsultantAssignment: {
    success: boolean;
    message: string;
    assignment?: CareerConsultantAssignment | null;
  };
};

export type UpsertSectionConsultantAssignmentVariables = {
  input: {
    sectionId: string;
    backofficeUserId: string;
    isActive?: boolean;
  };
};

export const UPSERT_SECTION_CONSULTANT_ASSIGNMENT_MUTATION: TypedDocumentNode<
  UpsertSectionConsultantAssignmentResponse,
  UpsertSectionConsultantAssignmentVariables
> = gql`
  mutation UpsertSectionConsultantAssignment($input: BackofficeUpsertSectionConsultantAssignmentInput!) {
    upsertSectionConsultantAssignment(input: $input) {
      success
      message
      assignment {
        id
        isActive
        section {
          id
          name
        }
        consultant {
          id
          name
          email
        }
      }
    }
  }
`;

export type DeleteSectionConsultantAssignmentResponse = {
  deleteSectionConsultantAssignment: {
    success: boolean;
    message: string;
    assignment?: CareerConsultantAssignment | null;
  };
};

export type DeleteSectionConsultantAssignmentVariables = {
  id: string;
};

export const DELETE_SECTION_CONSULTANT_ASSIGNMENT_MUTATION: TypedDocumentNode<
  DeleteSectionConsultantAssignmentResponse,
  DeleteSectionConsultantAssignmentVariables
> = gql`
  mutation DeleteSectionConsultantAssignment($id: ID!) {
    deleteSectionConsultantAssignment(id: $id) {
      success
      message
      assignment {
        id
        isActive
        section {
          id
          name
        }
        consultant {
          id
          name
          email
        }
      }
    }
  }
`;

export type UpdateSectionCoverageRoleResponse = {
  success: boolean;
  message: string;
  section?: CoverageSection | null;
};

export type UpdateSectionCoverageRoleVariables = {
  input: {
    sectionId: string;
    backofficeUserId?: string | null;
  };
};

const coverageSectionFields = gql`
  fragment ConfigurationCoverageSectionFields on BackofficeSection {
    id
    name
    recruiter {
      id
      name
      email
    }
    playerEngagementConsultant {
      id
      name
      email
    }
  }
`;

export type UpdateSectionRecruiterResponse = {
  updateSectionRecruiter: UpdateSectionCoverageRoleResponse;
};

export const UPDATE_SECTION_RECRUITER_MUTATION: TypedDocumentNode<
  UpdateSectionRecruiterResponse,
  UpdateSectionCoverageRoleVariables
> = gql`
  mutation UpdateSectionRecruiter($input: BackofficeUpdateSectionCoverageRoleInput!) {
    updateSectionRecruiter(input: $input) {
      success
      message
      section {
        ...ConfigurationCoverageSectionFields
      }
    }
  }
  ${coverageSectionFields}
`;

export type UpdateSectionPlayerEngagementConsultantResponse = {
  updateSectionPlayerEngagementConsultant: UpdateSectionCoverageRoleResponse;
};

export const UPDATE_SECTION_PLAYER_ENGAGEMENT_CONSULTANT_MUTATION: TypedDocumentNode<
  UpdateSectionPlayerEngagementConsultantResponse,
  UpdateSectionCoverageRoleVariables
> = gql`
  mutation UpdateSectionPlayerEngagementConsultant($input: BackofficeUpdateSectionCoverageRoleInput!) {
    updateSectionPlayerEngagementConsultant(input: $input) {
      success
      message
      section {
        ...ConfigurationCoverageSectionFields
      }
    }
  }
  ${coverageSectionFields}
`;

export type UpsertMessageTemplateResponse = {
  upsertMessageTemplate: {
    success: boolean;
    message: string;
    messageTemplate?: MessageTemplate | null;
  };
};

export type UpsertMessageTemplateVariables = {
  input: {
    templateType: MessageTemplateType;
    subject: string;
    body: string;
    signature: string;
  };
};

export const UPSERT_MESSAGE_TEMPLATE_MUTATION: TypedDocumentNode<
  UpsertMessageTemplateResponse,
  UpsertMessageTemplateVariables
> = gql`
  mutation UpsertMessageTemplate($input: BackofficeUpsertMessageTemplateInput!) {
    upsertMessageTemplate(input: $input) {
      success
      message
      messageTemplate {
        id
        templateType
        subject
        body
        signature
        updatedAt
      }
    }
  }
`;
